import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, Select, Switch, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { TICKETS_KEY, ticketErrorText, useTicketSettings } from '../lib/data';

const MUTED = 'var(--rr-color-text-muted)';
/** As the service allows (8 October 2026): up to eight letters, digits, hyphens, underscores or slashes, such as the default B- or T-. */
const PREFIX = /^[A-Za-z0-9/_-]{0,8}$/;

export type SettingsSection = 'general' | 'updates' | 'satisfaction';

type Draft = Omit<tickets.SettingsUpdate, 'csat_question' | 'auto_close_days'> & { csat_question: string; auto_close_days: number | '' };

function toDraft(s: tickets.Settings): Draft {
  return {
    number_prefix: s.number_prefix,
    visibility: s.visibility,
    notify_client: { ...s.notify_client },
    csat_enabled: s.csat_enabled,
    csat_question: s.csat_question ?? '',
    auto_close_days: s.auto_close_days ?? '',
    reopen_on_reply: s.reopen_on_reply
  };
}

/** General, client updates and satisfaction: the three parts of the one settings record, saved together. */
export function SettingsForm({ section, canEdit }: { section: SettingsSection; canEdit: boolean }) {
  const t = useT();
  const qc = useQueryClient();
  const q = useTicketSettings();
  const saved = q.data?.data;
  const [d, setD] = useState<Draft | null>(null);
  const [tried, setTried] = useState(false);

  useEffect(() => { if (saved) setD(toDraft(saved)); }, [saved]);
  const set = (patch: Partial<Draft>) => setD((x) => (x ? { ...x, ...patch } : x));
  const dirty = !!saved && !!d && JSON.stringify(d) !== JSON.stringify(toDraft(saved));
  const prefixBad = !!d && !PREFIX.test(d.number_prefix.trim());

  const save = useMutation({
    mutationFn: () => tickets.saveSettings({
      number_prefix: d!.number_prefix.trim(),
      visibility: d!.visibility,
      notify_client: d!.notify_client,
      csat_enabled: d!.csat_enabled,
      csat_question: d!.csat_question.trim() || null,
      auto_close_days: d!.auto_close_days === '' || d!.auto_close_days === 0 ? null : d!.auto_close_days,
      reopen_on_reply: d!.reopen_on_reply
    }),
    onSuccess: (res) => {
      qc.setQueryData([TICKETS_KEY, 'settings'], res);
      notifySuccess(t('tickets.settings.toast.saved'), t('tickets.settings.title'));
    },
    onError: (e) => notifyError(ticketErrorText(t, e), t('tickets.settings.title'))
  });

  function submit() {
    setTried(true);
    if (!canEdit || prefixBad) return;
    save.mutate();
  }

  if (q.error) {
    return (
      <Alert color="error" role="alert">
        <Group gap="sm" justify="space-between" wrap="wrap">
          <span>{ticketErrorText(t, q.error)}</span>
          <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
        </Group>
      </Alert>
    );
  }
  if (!d || !saved) return <Skeleton height={260} />;
  const ro = !canEdit;

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <Stack gap="lg">
        {section === 'general' && (
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>{t('tickets.settings.general')}</Title>
              <TextInput
                label={t('tickets.settings.prefix')}
                value={d.number_prefix}
                onChange={(e) => set({ number_prefix: e.currentTarget.value.toUpperCase() })}
                maxLength={8}
                description={t('tickets.settings.prefixHint', { example: `${d.number_prefix.trim()}1042` })}
                error={tried && prefixBad ? t('tickets.settings.error.prefix') : undefined}
                disabled={ro}
              />
              <Select
                label={t('tickets.settings.visibility')}
                value={d.visibility}
                onChange={(e) => set({ visibility: e.currentTarget.value as tickets.Visibility })}
                data={[
                  { value: 'all', label: t('tickets.settings.visibility.all') },
                  { value: 'team', label: t('tickets.settings.visibility.team') }
                ]}
                description={t('tickets.settings.visibilityHint')}
                disabled={ro}
              />
              <NumberInput
                label={t('tickets.settings.autoClose')}
                value={d.auto_close_days}
                onChange={(v) => set({ auto_close_days: v })}
                min={0}
                max={365}
                description={t('tickets.settings.autoCloseHint')}
                disabled={ro}
              />
              <Switch
                label={t('tickets.settings.reopen')}
                description={t('tickets.settings.reopenHint')}
                checked={d.reopen_on_reply}
                onChange={(on) => set({ reopen_on_reply: on })}
                disabled={ro}
              />
            </Stack>
          </Card>
        )}

        {section === 'updates' && (
          <Card withBorder>
            <Stack gap="md">
              <Stack gap="xs">
                <Title order={4}>{t('tickets.settings.updates')}</Title>
                <Text role="bodySmall" c={MUTED}>{t('tickets.settings.updatesIntro')}</Text>
              </Stack>
              <Switch label={t('tickets.settings.notifyCreated')} description={t('tickets.settings.notifyCreatedHint')}
                checked={d.notify_client.created} onChange={(on) => set({ notify_client: { ...d.notify_client, created: on } })} disabled={ro} />
              <Switch label={t('tickets.settings.notifyStatus')} description={t('tickets.settings.notifyStatusHint')}
                checked={d.notify_client.status_changed} onChange={(on) => set({ notify_client: { ...d.notify_client, status_changed: on } })} disabled={ro} />
              <Switch label={t('tickets.settings.notifySolved')} description={t('tickets.settings.notifySolvedHint')}
                checked={d.notify_client.solved} onChange={(on) => set({ notify_client: { ...d.notify_client, solved: on } })} disabled={ro} />
              <Text role="caption" c={MUTED}>{t('tickets.settings.updatesHint')}</Text>
            </Stack>
          </Card>
        )}

        {section === 'satisfaction' && (
          <Card withBorder>
            <Stack gap="md">
              <Stack gap="xs">
                <Title order={4}>{t('tickets.settings.satisfaction')}</Title>
                <Text role="bodySmall" c={MUTED}>{t('tickets.settings.satisfactionIntro')}</Text>
              </Stack>
              <Switch label={t('tickets.settings.csatEnabled')} checked={d.csat_enabled} onChange={(on) => set({ csat_enabled: on })} disabled={ro} />
              <Textarea
                label={t('tickets.settings.csatQuestion')}
                value={d.csat_question}
                onChange={(e) => set({ csat_question: e.currentTarget.value })}
                placeholder={t('tickets.settings.csatDefault')}
                minRows={2}
                maxLength={300}
                description={t('tickets.settings.csatQuestionHint')}
                disabled={ro || !d.csat_enabled}
              />
            </Stack>
          </Card>
        )}

        {canEdit && (
          <Group gap="sm">
            <Button type="submit" loading={save.isPending} disabled={!dirty}>{t('action.saveChanges')}</Button>
            {tried && prefixBad && <Text role="caption" c="var(--rr-color-error)">{t('tickets.settings.fixFirst')}</Text>}
          </Group>
        )}
      </Stack>
    </form>
  );
}
